import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Container,
  Card,
  CardContent,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import NotFound from '../components/NotFound';
import axios from 'axios';

interface PublicSubmissionData {
  id: string;
  uniqueId: string;
  editCode: string;
  formTitle: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  hasPayment: boolean;
  hasBeenEdited: boolean;
}

const ViewSubmission: React.FC = () => {
  const { uniqueId } = useParams<{ uniqueId: string }>();
  const navigate = useNavigate();
  
  const [submission, setSubmission] = useState<PublicSubmissionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editCode, setEditCode] = useState('');
  const [editCodeError, setEditCodeError] = useState('');

  useEffect(() => {
    const loadSubmission = async () => {
      try {
        setLoading(true);
        setError(null);
        setNotFound(false);

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/submissions/public/${uniqueId}`
        );

        setSubmission(response.data);

      } catch (error: any) {
        console.error('Error loading submission:', error);
        if (error.response?.status === 404) {
          setNotFound(true);
        } else {
          setError('Failed to load submission. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (uniqueId) {
      loadSubmission();
    }
  }, [uniqueId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getTimeSinceEdit = (updatedAt: string, createdAt: string) => {
    const updated = new Date(updatedAt);
    const created = new Date(createdAt);
    const diffInMinutes = Math.floor((updated.getTime() - created.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minutes after submission`;
    } else if (diffInMinutes < 1440) { // Less than 24 hours
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours} hour${hours > 1 ? 's' : ''} after submission`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return `${days} day${days > 1 ? 's' : ''} after submission`;
    }
  };

  const handleEditSubmission = () => {
    setEditModalOpen(true);
    setEditCode('');
    setEditCodeError('');
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditCode('');
    setEditCodeError('');
  };

  const handleSubmitEditCode = () => {
    const trimmedCode = editCode.trim();
    
    if (!trimmedCode) {
      setEditCodeError('Please enter your edit code');
      return;
    }
    
    if (!/^\d{6}$/.test(trimmedCode)) {
      setEditCodeError('Edit code must be exactly 6 digits');
      return;
    }
    
    // Navigate to edit page
    navigate(`/edit-submission/${uniqueId}/${trimmedCode}`);
    handleCloseEditModal();
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (notFound) {
    return (
      <NotFound
        title="Submission Not Found"
        message="The submission you're looking for was not found. Please check your unique ID and try again."
        showHomeButton={true}
        customAction={{
          label: "Try Again",
          onClick: () => window.location.reload()
        }}
      />
    );
  }

  if (error || !submission) {
    return (
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ p: 4, mt: 4, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error || 'Submission not found'}
          </Alert>
          <Button
            variant="contained"
            onClick={() => navigate('/')}
            startIcon={<HomeIcon />}
          >
            Go to Homepage
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 4, mb: 4 }}>
        {/* Header */}
        <Box mb={4} textAlign="center">
          <Typography variant="h4" component="h1" gutterBottom>
            <ViewIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Submission Details
          </Typography>
        </Box>

        {/* Submission Info */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {submission.formTitle}
            </Typography>
            
            <Divider sx={{ my: 2 }} />
            
            <Box display="flex" flexDirection="column" gap={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body1">
                  <strong>Unique ID:</strong>
                </Typography>
                <Typography variant="body2" fontFamily="monospace">
                  {submission.uniqueId}
                </Typography>
              </Box>

              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body1">
                  <strong>Status:</strong>
                </Typography>
                <Chip
                  label={submission.status}
                  color={getStatusColor(submission.status) as any}
                  size="small"
                />
              </Box>

              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body1">
                  <strong>Submitted:</strong>
                </Typography>
                <Typography variant="body2">
                  {new Date(submission.createdAt).toLocaleString()}
                </Typography>
              </Box>

              {submission.hasBeenEdited && (
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1">
                    <strong>Last Edited:</strong>
                  </Typography>
                  <Box display="flex" flexDirection="column" alignItems="flex-end">
                    <Typography variant="body2">
                      {new Date(submission.updatedAt).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getTimeSinceEdit(submission.updatedAt, submission.createdAt)}
                    </Typography>
                    <Chip
                      label="Edited"
                      color="warning"
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                </Box>
              )}

              {/* <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body1">
                  <strong>Edit Code:</strong>
                </Typography>
                <Typography variant="body2">
                  {submission?.editCode}
                </Typography>
              </Box> */}

              {submission.hasPayment && (
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1">
                    <strong>Payment:</strong>
                  </Typography>
                  <Chip
                    label="Required"
                    color="info"
                    size="small"
                  />
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Actions */}
        <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
          <Button
            variant="contained"
            color="primary"
            onClick={handleEditSubmission}
            startIcon={<EditIcon />}
            size="large"
          >
            Edit Submission
          </Button>
          
          {/* <Button
            variant="outlined"
            onClick={() => navigate('/')}
            startIcon={<HomeIcon />}
            size="large"
          >
            Go to Homepage
          </Button> */}
        </Box>

        {/* Instructions */}
        <Alert severity="info" sx={{ mt: 4 }}>
          <Typography variant="body2" gutterBottom>
            <strong>About This Submission:</strong>
          </Typography>
          <Typography variant="body2">
            • This page shows basic information about your submission<br/>
            • To edit your submission, you need both the Unique ID and your 6-digit edit code<br/>
            • The edit code shows when you first submitted the form or can be provided by the admin<br/>
            • If you've edited your submission, the "Last Edited" date will show when changes were made<br/>
            • Contact support if you've lost your edit code
          </Typography>
        </Alert>
      </Paper>

      {/* Edit Code Modal */}
      <Dialog 
        open={editModalOpen} 
        onClose={handleCloseEditModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <EditIcon />
            <Typography variant="h6">Edit Submission</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Enter your 6-digit edit code to modify this submission. The edit code shows when you first submitted the form or can be provided by the admin.
          </Typography>
          
          <TextField
            autoFocus
            label="Edit Code"
            type="text"
            fullWidth
            variant="outlined"
            value={editCode}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 6); // Only allow digits, max 6
              setEditCode(value);
              if (editCodeError) setEditCodeError('');
            }}
            error={!!editCodeError}
            helperText={editCodeError || 'Enter the 6-digit code.'}
            placeholder="123456"
            inputProps={{
              maxLength: 6,
              style: { 
                textAlign: 'center',
                fontSize: '1.2rem',
                letterSpacing: '0.2em',
                fontFamily: 'monospace'
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSubmitEditCode();
              }
            }}
          />
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseEditModal} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitEditCode} 
            variant="contained"
            disabled={editCode.length !== 6}
          >
            Continue to Edit
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ViewSubmission;
